import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Drawer, Button, Text, Heading, TextField } from "@foris/avocado-suite";
import { splitSlot } from "@context/useProgrammingEditStore";
import styles from "./splitBlockDrawer.module.scss";
import { useLayoutStore } from "@context/useLayoutStore";
import { Icon } from "@foris/avocado-icons";

interface SplitBlockDrawerProps {
  slotId: string;
  totalBlocks: number;
  isOpen: boolean;
  /** Read-only: the split configuration is visible but cannot be changed or applied. */
  isReadOnly?: boolean;
  onClose: () => void;
}

const MIN_BLOCKS_PER_PART = 3;
const HOURS_PER_BLOCK = 0.5;

const distributeBlocks = (total: number, parts: number): number[] => {
  const base = Math.floor(total / parts);
  // Round down to nearest multiple of 3
  const baseMultiple = Math.floor(base / 3) * 3;
  const blocks = Array(parts).fill(baseMultiple);
  // Assign remaining blocks to the last part
  const assigned = baseMultiple * parts;
  if (assigned < total) {
    const remainder = total - assigned;
    // remainder must also be a multiple of 3 for a valid split
    blocks[parts - 1] = baseMultiple + remainder;
  }
  return blocks;
};

const SplitBlockDrawer: React.FC<SplitBlockDrawerProps> = ({
  slotId,
  totalBlocks,
  isOpen,
  isReadOnly = false,
  onClose,
}) => {
  const { t } = useTranslation();
  const heightGap = useLayoutStore(state => state.visibleHeaderPixels);
  const minParts = 2;
  const maxParts = Math.floor(totalBlocks / MIN_BLOCKS_PER_PART);

  const [numberOfParts, setNumberOfParts] = useState<number | "">(minParts);
  const [blocksPerPart, setBlocksPerPart] = useState<(number | "")[]>(() =>
    distributeBlocks(totalBlocks, minParts),
  );

  useEffect(() => {
    if (numberOfParts !== "") {
      setBlocksPerPart(distributeBlocks(totalBlocks, numberOfParts));
    }
  }, [numberOfParts, totalBlocks]);

  const handleNumberOfPartsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === "") return setNumberOfParts("");
    const parsed = parseInt(raw);
    if (!isNaN(parsed)) setNumberOfParts(Math.min(maxParts, Math.max(minParts, parsed)));
  };

  const handleBlocksChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const value = raw === "" ? "" : parseInt(raw) || 0;
    setBlocksPerPart(prev => prev.map((b, i) => (i === index ? value : b)));
  };

  const isMultipleOf3 = (n: number | "") => typeof n === "number" && n > 0 && n % 3 === 0;
  const numericBlocks = blocksPerPart.map(b => (typeof b === "number" ? b : 0));
  const totalAssigned = numericBlocks.reduce((acc, b) => acc + b, 0);
  const isTotalValid = totalAssigned === totalBlocks;
  const arePartsValid = blocksPerPart.every(isMultipleOf3);
  const isValid = isTotalValid && arePartsValid && numberOfParts !== "";

  const handleConfirm = () => {
    splitSlot(slotId, { parts: numberOfParts as number, blocksPerPart: numericBlocks });
    onClose();
  };

  const handleClose = () => {
    setNumberOfParts(minParts);
    setBlocksPerPart(distributeBlocks(totalBlocks, minParts));
    onClose();
  };

  return (
    <Drawer
      className={styles.splitBlockDrawer}
      title={<Heading type="h3">{t("programming-edit.split-block.title")}</Heading>}
      isOpen={isOpen}
      onClose={handleClose}
      heightGap={heightGap}
      closeOnOverlayClick
      footer={
        <div className={styles.splitBlockDrawer_footer}>
          {isReadOnly ? (
            <Button variant="ghost" onClick={handleClose}>
              {t("programming-edit.split-block.close")}
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={handleClose}>
                {t("programming-edit.split-block.cancel")}
              </Button>
              <Button variant="primary" onClick={handleConfirm} disabled={!isValid}>
                {t("programming-edit.split-block.confirm")}
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className={styles.splitBlockDrawer_content}>
        <div className={styles.splitBlockDrawer_fieldGroup}>
          <Text type="md" weight="medium" className={styles.splitBlockDrawer_partLabel}>
            {t("programming-edit.split-block.parts-question")}
          </Text>
          <div className={styles.splitBlockDrawer_partsRows}>
            <div className={styles.splitBlockDrawer_partInput}>
              <TextField
                type="number"
                min={minParts}
                max={maxParts}
                value={numberOfParts}
                disabled={isReadOnly}
                onChange={handleNumberOfPartsChange}
                className={styles.splitBlockDrawer_partInput}
              />
            </div>
            <div className={styles.splitBlockDrawer_hint}>
              <Icon name="circle-info" size="sm" color="neutral-70" />
              <Text type="sm">
                {maxParts !== minParts
                  ? t("programming-edit.split-block.parts-hint", { min: minParts, max: maxParts })
                  : t("programming-edit.split-block.parts-hint-single", { min: minParts })}
              </Text>
            </div>
          </div>
        </div>

        <div className={styles.splitBlockDrawer_fieldGroup}>
          <Text type="md" weight="medium" className={styles.splitBlockDrawer_partLabel}>
            {t("programming-edit.split-block.blocks-question", { totalBlocks })}
          </Text>
          <div className={styles.splitBlockDrawer_partsRows}>
            {blocksPerPart.map((blocks, index) => (
              <div key={index} className={styles.splitBlockDrawer_partRow}>
                <Text type="md" weight="medium" className={styles.splitBlockDrawer_partLabel}>
                  {t("programming-edit.split-block.part-label", { part: index + 1 })}
                </Text>
                <div className={styles.splitBlockDrawer_partInput}>
                  <TextField
                    type="number"
                    min={3}
                    step={3}
                    value={blocks}
                    disabled={isReadOnly}
                    onChange={e => handleBlocksChange(index, e)}
                  />
                </div>
                <Text type="sm" className={styles.splitBlockDrawer_partHours}>
                  {t("programming-edit.split-block.part-hours", {
                    hours: ((typeof blocks === "number" ? blocks : 0) * HOURS_PER_BLOCK).toFixed(1),
                  })}
                </Text>
              </div>
            ))}
          </div>

          <div className={styles.splitBlockDrawer_totalSummary}>
            <Text type="sm" className={styles.splitBlockDrawer_totalLabel}>
              {t("programming-edit.split-block.total-label")}
            </Text>
            <Text
              type="md"
              weight="medium"
              className={`${styles.splitBlockDrawer_totalValue}${
                !isTotalValid ? ` ${styles.splitBlockDrawer_totalValue__error}` : ""
              }`}
            >
              {t("programming-edit.split-block.total-value", {
                assigned: totalAssigned,
                total: totalBlocks,
              })}
            </Text>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default SplitBlockDrawer;
